import copy from './generated/copy.json';
const string = (value) => typeof value === 'string';
const optionalString = (value) => value == null || string(value);
const count = (value) => Number.isInteger(value) && value >= 0;
const rating = (value) => Number.isFinite(value) && value >= 0 && value <= 5;
const identity = (value) =>
  (typeof value === 'number' && Number.isFinite(value)) || (string(value) && value.length > 0);
const bucket = (item) =>
  item &&
  string(item.label) &&
  count(item.count) &&
  rating(item.averageRating) &&
  ['imageUrl', 'artistSourceId', 'artistSource', 'artistSlug'].every((key) =>
    optionalString(item[key]),
  );
export const tasteTranslate =
  (locale) =>
  (key, params = {}) =>
    (copy[locale === 'en' ? 'en' : 'ko'][key] ?? key).replace(/\{\{(\w+)\}\}/g, (_, name) =>
      String(params[name] ?? ''),
    );
export function tastePayload(response) {
  const data = response?.data?.data ?? response?.data;
  const numbers = ['ratingCount', 'reviewCount', 'averageRating', 'communityDifference'];
  if (
    !data ||
    typeof data.isReady !== 'boolean' ||
    numbers.some((key) => !Number.isFinite(data[key])) ||
    !['STRICT', 'GENEROUS', 'BALANCED', 'INSUFFICIENT'].includes(data.ratingStyle) ||
    ['topGenres', 'topDecades', 'topArtists', 'regions', 'mostDivergentAlbums'].some(
      (key) => !Array.isArray(data[key]),
    ) ||
    !data.ratingDistribution ||
    typeof data.ratingDistribution !== 'object' ||
    Array.isArray(data.ratingDistribution) ||
    !count(data.ratingCount) ||
    !count(data.reviewCount) ||
    !rating(data.averageRating) ||
    (data.dominantRating != null && !rating(data.dominantRating)) ||
    Object.values(data.ratingDistribution).some((value) => !count(value)) ||
    ['topGenres', 'topDecades', 'topArtists'].some((key) => !data[key].every(bucket)) ||
    !data.regions.every(
      (item) => item && string(item.label) && count(item.count) && Number.isFinite(item.percentage),
    ) ||
    !data.mostDivergentAlbums.every(
      (item) =>
        item &&
        identity(item.albumId) &&
        string(item.albumTitle) &&
        optionalString(item.albumSlug) &&
        optionalString(item.imageUrl) &&
        rating(item.myRating) &&
        rating(item.communityRating) &&
        Number.isFinite(item.difference),
    ) ||
    (data.persona != null &&
      (!string(data.persona.key) ||
        !string(data.persona.title) ||
        !Array.isArray(data.persona.evidence) ||
        !data.persona.evidence.every(string) ||
        (data.persona.evidenceMessages != null &&
          (!Array.isArray(data.persona.evidenceMessages) ||
            !data.persona.evidenceMessages.every(
              (item) => item && string(item.code) && item.params && typeof item.params === 'object',
            )))))
  )
    throw Error('Invalid taste response');
  return data;
}
export function featurePayload(response) {
  const data = response?.data?.data ?? response?.data;
  // The API omits null representative fields; an empty object is a valid empty profile.
  if (!data || typeof data !== 'object' || Array.isArray(data))
    throw Error('Invalid music feature response');
  const review = data.representativeReview,
    playlist = data.representativePlaylist;
  if (
    (review != null &&
      (!review.album ||
        !(identity(review.album.id) || identity(review.album.slug)) ||
        !string(review.album.title) ||
        !optionalString(review.album.imageUrl) ||
        !review.review ||
        !identity(review.review.id) ||
        !rating(review.review.rating) ||
        (review.artist != null && !string(review.artist.name)))) ||
    (playlist != null &&
      (!(identity(playlist.id) || identity(playlist.slug)) ||
        !string(playlist.title) ||
        !['ALBUM', 'TRACK'].includes(playlist.type) ||
        !count(playlist.type === 'ALBUM' ? playlist.albumCount : playlist.trackCount)))
  )
    throw Error('Invalid music feature response');
  return data;
}
export function regionSplit(data) {
  const domestic = data.regions.find((x) => ['국내', 'KR', 'Korean'].includes(x.label))?.count ?? 0;
  const overseas =
    data.regions.find((x) => ['해외', 'GLOBAL', 'International'].includes(x.label))?.count ?? 0;
  const classified = domestic + overseas;
  const percent = classified ? Math.round((domestic / classified) * 100) : 0;
  return { domestic: percent, overseas: classified ? 100 - percent : 0, classified };
}
export function tasteArtistLink(artist) {
  const state = { sourceId: artist.artistSourceId, sourceType: artist.artistSource };
  if (artist.artistSlug)
    return {
      to: `/artists/${encodeURIComponent(artist.artistSlug)}${artist.artistSourceId ? `?${new URLSearchParams({ source_id: artist.artistSourceId })}` : ''}`,
      state,
    };
  if (artist.artistSourceId)
    return {
      to: `/artists/${artist.artistSource?.toLowerCase() === 'spotify' ? 'spotify' : 'apple_music'}/${encodeURIComponent(artist.artistSourceId)}`,
      state,
    };
  return { to: `/search?${new URLSearchParams({ q: artist.label })}` };
}
export const ratingProgress = (count) => Math.max(0, Math.min(1, count / 5));
export const progressWindow = (value, start, end) =>
  Math.max(0, Math.min(1, (value - start) / (end - start)));
export const sampledNumber = (progress, value, start = 0, end = 1, from = 0) =>
  from + ((value - from) * Math.round(progressWindow(progress, start, end) * 12)) / 12;
